/* For SheerId Integration.
See https://fox.quip.com/vvStArDEOQMK/SheerID-Integration-FE for more information. */
import {
  SHEER_MILITARY_PACKAGE_ID,
  SHEER_FIRST_RESPONDER_PACKAGE_ID,
  SHEER_FIRST_RESPONDER_2YR_PACKAGE_ID,
  SHEER_MILITARY_UPGRADE,
  SHEER_MILITARY_UPGRADE_ALT,
  SHEER_MILITARY_UPGRADE_CHURNED,
  SHEER_MILITARY_TYPE,
  SHEER_FIRST_RESPONDER_TYPE,
  SHEER_FIRST_RESPONDER_1YR_UPGRADE,
  SHEER_FIRST_RESPONDER_MONTHLY_UPGRADE,
  VERIZON_SHEER_VERIFICATION_TEST,
  VERIZON_SHEER_VERIFICATION_MILITARY,
} from 'Utils/Constants';
import { addSheerVerificationId, cancelVerification } from 'Api/SheerIdService';
import { getStoreDispatch, getStoreState } from './Store';
import { clearSheerId, getUserSheerInfo } from 'State/Integrations/IntegrationsActions';
import { localStorageGet, localStorageSet } from './Storage';

const SHEER_TYPE_IDS = {
  [SHEER_MILITARY_TYPE]: SHEER_MILITARY_PACKAGE_ID,
  [SHEER_FIRST_RESPONDER_TYPE]: SHEER_FIRST_RESPONDER_PACKAGE_ID,
};

export const SHEER_PAGE_TYPES = {
  FORM: 'FORM',
  DOC_UPLOAD: 'DOC_UPLOAD',
  REVIEW: 'REVIEW',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  INELIGIBLE: 'INELIGIBLE',
};

export const SHEER_PROGRAM_NAMES = {
  military: 'MILITARY',
  firstResponder: 'FIRST_RESPONDER',
};

export const SHEER_PAGE_STATUS = {
  success: SHEER_PAGE_TYPES.SUCCESS,
  docUpload: SHEER_PAGE_TYPES.DOC_UPLOAD,
  pending: SHEER_PAGE_TYPES.REVIEW,
  error: SHEER_PAGE_TYPES.ERROR,
  ineligible: SHEER_PAGE_TYPES.INELIGIBLE,
  collectActiveMilitaryPersonalInfo: SHEER_PAGE_TYPES.FORM,
  collectInactiveMilitaryPersonalInfo: SHEER_PAGE_TYPES.FORM,
};

export const SHEER_PAGE_ERROR_MESSAGES = {
  // Page default error codes
  noRemainingRewardCodes:
    'Due to tremendous demand, the offer is no longer available. Please check tomorrow to see if it is available.',
  invalidStep: 'Invalid step',
  apiRateLimitExceeded: 'Unable to process your request at this time, please try again later,',
  expiredProgram:
    'Due to tremendous demand, the offer is no longer available. Please check tomorrow to see if it is available.',
  expiredVerification: 'This verification is expired. Please submit a new verification.',
  inactiveProgram:
    'Due to tremendous demand, the offer is no longer available. Please check tomorrow to see if it is available.',
  internalServerError: 'Internal server error',
  invalidRequest: 'Invalid request',
  requestTimeout: 'Request timeout',
  unauthorizedAccountStatus:
    'This offer has been temporarily discontinued. Please contact the brand’s customer support for more information.',
  unauthorizedDomain: 'Unauthorized domain',
  unknownError: 'Unknown Error',
  // doc upload error codes
  docReviewLimitExceeded: 'Document review limit exceeded',
  invalidFileSizeEmpty: 'One or more files is empty',
  invalidFileSizeMax: 'One or more files is too large (max: 10MB)',
  invalidNumberOfFiles: 'Maximum number of files exceeded (max: 5)',
  unsupportedDocMimeType: 'One or more files is an unsupported format',
  // failsafe form error codes
  invalidAddress: 'Invalid address',
  invalidBirthDate: 'Invalid birth date',
  invalidCity: 'Invalid city',
  invalidCountry: 'Invalid country',
  invalidDischargeDate: 'Invalid discharge date',
  invalidEmail: 'Invalid email',
  invalidFirstName: 'Invalid first name',
  invalidFirstResponderStatus: 'Invalid first responder status',
  invalidLastName: 'Invalid last name',
  invalidMemberId: 'Invalid ID Number',
  invalidMilitaryStatus: 'Invalid military status',
  invalidOptIn: 'Must be selected to continue',
  invalidOrganization: 'You must select an organization from the list',
  invalidPhoneNumber: 'Invalid mobile number',
  invalidPostalCode: 'Invalid postal code',
  invalidProgram: 'Invalid program',
  invalidState: 'Invalid state',
  invalidStatus: 'Invalid status',
  invalidStatuses: 'Invalid status',
  maxMetadataLengthExceeded: 'Maximum length of metadata value exceeded',
  maxMetadataValuesExceeded: 'Maximum number of metadata values exceeded',
  dischargeDateBeforeBirthDate: 'Discharge date before birthdate',
  futureBirthDate: 'Future birthdate',
  futureDischargeDate: 'Future discharge date',
  marketConsentRequired: 'Market consent required',
  outsideAgePerson: 'Birthdate is not eligible for this offer.',
  underagePerson: 'Persons under age 16 are not eligible for this offer.',
};

export const SHEER_PAGE_TITLES = {
  FORM: 'Verify Your Status',
  DOC_UPLOAD: 'Verify Your Status',
  REVIEW: 'Verify Your Status',
  SUCCESS: 'Congratulations!',
  ERROR: 'Sorry, There Was a Problem',
  INELIGIBLE: 'Good News!',
};

export const getSheerVerificationId = () => {
  return getStoreState('integrations').sheerId.verificationId;
};

export const getSheerIdProgram = () => {
  return getStoreState('integrations').sheerId.program;
};

export const getFreeSheerIdPlanFromType = () => {
  return SHEER_TYPE_IDS[getSheerIdProgram()];
};

export const exitSheerIdFlow = async (unlink = false) => {
  if (unlink) {
    try {
      await cancelVerification();
    } catch (e) {
      return Promise.reject(e);
    }
  }
  const dispatch = getStoreDispatch();
  dispatch(clearSheerId());
};

export const sendSheerVerificationId = async () => {
  const sheerVerificationId = getSheerVerificationId();
  if (sheerVerificationId) {
    await addSheerVerificationId(sheerVerificationId);
    exitSheerIdFlow();
  }
};

export const isMilitaryFreePlan = (packageID) => {
  return packageID === SHEER_MILITARY_PACKAGE_ID;
};

export const isFirstResponderFreePlan = (packageID) => {
  return packageID === SHEER_FIRST_RESPONDER_PACKAGE_ID;
};

export const isFirstResponderUpgradePlan = (packageID) =>
  [
    SHEER_FIRST_RESPONDER_MONTHLY_UPGRADE,
    SHEER_FIRST_RESPONDER_1YR_UPGRADE,
    SHEER_FIRST_RESPONDER_2YR_PACKAGE_ID,
  ].includes(packageID);

export const isMilitaryUpgradePlan = (packageID) => {
  return [SHEER_MILITARY_UPGRADE, SHEER_MILITARY_UPGRADE_ALT, SHEER_MILITARY_UPGRADE_CHURNED].includes(packageID);
};

export const isSheerIdPlan = (packageID) => {
  return isMilitaryPlan(packageID) || isFirstResponderPlan(packageID);
};

const isMilitaryPlan = (packageID) => {
  return isMilitaryFreePlan(packageID) || isMilitaryUpgradePlan(packageID);
};

const isFirstResponderPlan = (packageID) => {
  return isFirstResponderFreePlan(packageID) || isFirstResponderUpgradePlan(packageID);
};

export const isEligibleSheerIdUpgradePlan = (subscription, plan) => {
  const isUpgrade = plan.tier > subscription.activeSubscriptionTier;

  let isAllowedPlan = false;

  if (isMilitaryPlan(subscription.activeSubscriptionId)) {
    isAllowedPlan = plan.packageID === SHEER_MILITARY_UPGRADE || plan.packageID === SHEER_MILITARY_UPGRADE_ALT;

    // If current first responder plan is the free trial plan, only show 2 YR upgrade
  } else if (subscription.activeSubscriptionId === SHEER_FIRST_RESPONDER_PACKAGE_ID) {
    isAllowedPlan = plan.packageID === SHEER_FIRST_RESPONDER_2YR_PACKAGE_ID;
  } else if (isFirstResponderPlan(subscription.activeSubscriptionId)) {
    isAllowedPlan =
      plan.packageID === SHEER_FIRST_RESPONDER_1YR_UPGRADE || plan.packageID === SHEER_FIRST_RESPONDER_2YR_PACKAGE_ID;
  }

  return isUpgrade && isAllowedPlan;
};

export const isSheerIdFreePlan = (packageID) => {
  return isMilitaryFreePlan(packageID) || isFirstResponderFreePlan(packageID);
};

export const isSheerIdDowngrade = (activeSubscriptionPlanName = '') => {
  return (
    getSheerVerificationId() &&
    (activeSubscriptionPlanName.includes('Silver') || activeSubscriptionPlanName.includes('Gold'))
  );
};

export const isFirstResponderChurned = (isChurnedUser, restartChurnedUserDetails) =>
  isChurnedUser &&
  restartChurnedUserDetails &&
  restartChurnedUserDetails.churnedPlan &&
  (isFirstResponderFreePlan(restartChurnedUserDetails.churnedPlan.packageID) ||
    isFirstResponderUpgradePlan(restartChurnedUserDetails.churnedPlan.packageID));

export const isMilitaryChurned = (isChurnedUser, restartChurnedUserDetails) =>
  isChurnedUser &&
  restartChurnedUserDetails &&
  restartChurnedUserDetails.churnedPlan &&
  (isMilitaryFreePlan(restartChurnedUserDetails.churnedPlan.packageID) ||
    isMilitaryUpgradePlan(restartChurnedUserDetails.churnedPlan.packageID));

export const isSheerProductSKU = (productId) =>
  productId === VERIZON_SHEER_VERIFICATION_TEST || productId === VERIZON_SHEER_VERIFICATION_MILITARY;

export const getSheerProgramFromSKU = (productId) => {
  switch (productId) {
    case VERIZON_SHEER_VERIFICATION_MILITARY:
      return SHEER_MILITARY_TYPE;
    case VERIZON_SHEER_VERIFICATION_TEST:
      return SHEER_MILITARY_TYPE;
    //TODO add first responders when we have the verizon skus for first responder
  }
  return null;
};

/*
For non-bango existing users in middle of verification process, redirect them back to form on sign-in up to 3 times
https://teamfox.atlassian.net/browse/NATCOM-877
Per Adriana, this is a temporary solution to allow existing users to not always be redirected to sheerid flow
*/
export const getExistingUserRedirects = () => {
  return localStorageGet('sheerIdExistingRedirects') || 0;
};

export const incrementExistingUserRedirects = () => {
  localStorageSet('sheerIdExistingRedirects', Number(getExistingUserRedirects()) + 1);
};

export const shouldGetSheerIdPlans = async (isAuthenticated, activeSubscriptionId) => {
  let shouldGetSheerIdPlans = false;

  if (isAuthenticated) {
    try {
      const dispatch = getStoreDispatch();
      const { currentStep } = await dispatch(getUserSheerInfo());
      if (currentStep === 'success') shouldGetSheerIdPlans = true;
    } catch (error) {
      /*Per convo with BE, there are some users with a SheerId plan that for some reason
        don't have a verificationId in the BE, so getUserSheerInfo() would return an error
        even though they're SheerId verified. So looking at their active subscription.
        BE also looks at active subscription for these users in determining if should
        return SheerId plans for /packages endpoint.
        But we want to know if they're SheerId users before calling /packages endpoint,
        so checking their active sub below.
      */
      if (isSheerIdPlan(activeSubscriptionId)) {
        shouldGetSheerIdPlans = true;
      }
    }
  }
  return shouldGetSheerIdPlans;
};
